import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { EmptyState } from '@/components/data/EmptyState';
import { AuditChip } from '@/components/audit/AuditChip';
import { PromptForm } from '@/features/phase2/PromptForm';
import { SLAClock } from '@/features/phase2/SLAClock';

import { useApiError } from '@/lib/useApiError';
import { useUiStore } from '@/stores/ui';
import {
  usePromptSlaBreaches,
  usePromptsList,
  useResolvePromptReplySla,
} from '@/queries/phase2';
import type { PromptSlaBreach, PromptSummary } from '@/schemas/admin/phase2/prompts';

export const Route = createFileRoute('/_authed/phase2/prompts')({
  component: PromptsScreen,
});

function PromptsScreen() {
  const { t } = useTranslation('phase2');
  const locale = useUiStore((s) => s.locale);
  const breaches = usePromptSlaBreaches();
  const prompts = usePromptsList();
  const resolve = useResolvePromptReplySla();
  const resolveError = useApiError();

  const [creating, setCreating] = React.useState(false);
  const [resolving, setResolving] = React.useState<PromptSlaBreach | null>(null);

  function localizedTitle(p: PromptSummary): string {
    if (locale === 'eg-ar' && p.title_eg_ar) return p.title_eg_ar;
    if (locale === 'ar' && p.title_ar) return p.title_ar;
    return p.title_en ?? p.slug;
  }

  const promptColumns: readonly DataColumn<PromptSummary>[] = [
    {
      id: 'slug',
      header: t('prompts.col.slug', { defaultValue: 'Slug' }),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium">{localizedTitle(r)}</span>
          <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
            {r.slug}
          </span>
        </div>
      ),
      sortBy: (r) => r.slug,
    },
    {
      id: 'pillar',
      header: t('prompts.col.pillar', { defaultValue: 'Pillar' }),
      cell: (r) =>
        r.primary_pillar ? (
          <Badge tone="neutral">{r.primary_pillar}</Badge>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
      sortBy: (r) => r.primary_pillar ?? '',
    },
    {
      id: 'safety',
      header: t('prompts.col.safety', { defaultValue: 'Sensitivity' }),
      cell: (r) =>
        r.safety_level ? (
          <Badge tone={r.safety_level === 'sensitive' ? 'warn' : 'info'}>
            {r.safety_level}
          </Badge>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
      sortBy: (r) => r.safety_level ?? '',
    },
    {
      id: 'state',
      header: t('prompts.col.state', { defaultValue: 'State' }),
      cell: (r) => (
        <Badge tone={r.state === 'published' ? 'success' : r.state === 'draft' ? 'neutral' : 'warn'}>
          {r.state}
        </Badge>
      ),
      sortBy: (r) => r.state,
    },
    {
      id: 'replies',
      header: t('prompts.col.replies', { defaultValue: 'Replies' }),
      cell: (r) =>
        typeof r.reply_count === 'number' ? (
          <span dir="ltr" className="font-mono">
            {r.reply_count}
          </span>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
      align: 'end',
      sortBy: (r) => r.reply_count ?? -1,
    },
    {
      id: 'published',
      header: t('prompts.col.published', { defaultValue: 'Published' }),
      cell: (r) => <DateTime value={r.published_at ?? r.updated_at ?? null} />,
      sortBy: (r) => (r.published_at ? new Date(r.published_at) : null),
    },
  ];

  const columns: readonly DataColumn<PromptSlaBreach>[] = [
    {
      id: 'prompt',
      header: t('prompts.col.prompt', { defaultValue: 'Prompt' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.prompt_slug}
        </span>
      ),
      sortBy: (r) => r.prompt_slug,
    },
    {
      id: 'reply',
      header: t('prompts.col.reply', { defaultValue: 'Reply' }),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.reply_id.slice(0, 8)}…
        </span>
      ),
    },
    {
      id: 'created',
      header: t('prompts.col.created', { defaultValue: 'Posted' }),
      cell: (r) => <DateTime value={r.created_at} />,
      sortBy: (r) => (r.created_at ? new Date(r.created_at) : new Date(0)),
    },
    {
      id: 'sla',
      header: t('prompts.col.sla', { defaultValue: 'SLA' }),
      cell: (r) => <SLAClock dueAt={r.mod_attention_due_at ?? null} />,
      sortBy: (r) => (r.mod_attention_due_at ? new Date(r.mod_attention_due_at) : new Date(0)),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('prompts.col.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <Button size="sm" onClick={() => setResolving(r)}>
          {t('prompts.resolve', { defaultValue: 'Resolve' })}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('prompts.title', { defaultValue: 'Prompts' })}
          </h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
            {t('prompts.subtitle', {
              defaultValue:
                'Editorial prompts that surface across the consumer app. Sensitive prompts trigger a 2-hour moderator-attention SLA — breaches show up below.',
            })}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {t('prompts.new', { defaultValue: 'New prompt' })}
        </Button>
      </header>

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog">
            {t('prompts.tabs.catalog', { defaultValue: 'All prompts' })}
          </TabsTrigger>
          <TabsTrigger value="sla">
            {t('prompts.tabs.sla', { defaultValue: 'SLA breaches' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="catalog">
          <Card>
            <CardContent className="pt-6">
              {prompts.data === null ? (
                <EmptyState
                  title={t('prompts.catalogEndpointMissingTitle', {
                    defaultValue: 'Prompt list endpoint not exposed yet.',
                  })}
                  body={t('prompts.catalogEndpointMissingBody', {
                    defaultValue:
                      'The backend has not surfaced GET /admin/v1/prompts. Use the New prompt form below — it is idempotent on slug, so re-saving an existing slug updates it.',
                  })}
                />
              ) : (
                <DataTable<PromptSummary>
                  data={prompts.data ?? []}
                  columns={promptColumns}
                  rowKey={(r) => r.id}
                  loading={prompts.isLoading}
                  error={prompts.error ? resolveError(prompts.error) : null}
                  empty={{
                    title: t('prompts.catalogEmpty', { defaultValue: 'No prompts yet.' }),
                    body: t('prompts.catalogEmptyBody', {
                      defaultValue: 'Use the New prompt button to add one.',
                    }),
                  }}
                  initialSort={{ id: 'slug', direction: 'asc' }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="sla">
          <Card>
            <CardContent className="pt-6">
              <DataTable<PromptSlaBreach>
                data={breaches.data ?? []}
                columns={columns}
                rowKey={(r) => r.reply_id}
                loading={breaches.isLoading}
                error={breaches.error ? resolveError(breaches.error) : null}
                empty={{
                  title: t('prompts.slaEmpty', { defaultValue: 'Inbox zero on SLA breaches.' }),
                  body: t('prompts.slaEmptyBody', {
                    defaultValue: 'No sensitive-prompt replies are waiting for a moderator.',
                  }),
                }}
                initialSort={{ id: 'sla', direction: 'asc' }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('prompts.newTitle', { defaultValue: 'New prompt' })}</DialogTitle>
            <DialogDescription>
              {t('prompts.newBody', {
                defaultValue:
                  'Idempotent on slug. First transition to published stamps published_at; sensitive prompts trigger the SLA clock on every reply.',
              })}
            </DialogDescription>
          </DialogHeader>
          <PromptForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <SlaResolveDialog
        breach={resolving}
        open={Boolean(resolving)}
        onOpenChange={(o) => !o && setResolving(null)}
        loading={resolve.isPending}
        onConfirm={async (notes) => {
          if (!resolving) return;
          try {
            await resolve.mutateAsync({
              replyId: resolving.reply_id,
              ...(notes ? { input: { notes } } : {}),
            });
            toast.success(t('prompts.slaResolved', { defaultValue: 'SLA resolved.' }));
            setResolving(null);
          } catch (err) {
            const resolved = resolveError(err);
            toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
          }
        }}
      />
    </div>
  );
}

function SlaResolveDialog({
  breach,
  open,
  onOpenChange,
  loading,
  onConfirm,
}: {
  breach: PromptSlaBreach | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  onConfirm: (notes: string) => Promise<void>;
}) {
  const { t } = useTranslation('phase2');
  const [notes, setNotes] = React.useState('');
  React.useEffect(() => {
    if (!open) setNotes('');
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('prompts.slaResolveTitle', {
              defaultValue: 'Resolve SLA on reply',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('prompts.slaResolveBody', {
              defaultValue:
                'Marks mod_reviewed_at = now(). Use after a moderator has posted a supportive reply.',
            })}
          </DialogDescription>
        </DialogHeader>
        {breach ? (
          <p className="mb-3 text-xs text-[color:var(--color-fg-muted)]">
            {t('prompts.slaResolveContext', {
              defaultValue: 'Reply {reply} on prompt {prompt}.',
              reply: breach.reply_id.slice(0, 8) + '…',
              prompt: breach.prompt_slug,
            })}
          </p>
        ) : null}
        <div>
          <Label htmlFor="sla-notes">{t('prompts.slaNotes', { defaultValue: 'Notes (optional)' })}</Label>
          <textarea
            id="sla-notes"
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <AuditChip />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
              {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={() => void onConfirm(notes)} loading={loading}>
              {t('prompts.slaResolveCta', { defaultValue: 'Resolve' })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
